import { ReactComponent as OutlineChevronDown } from "assets/svg/outline-chevron-down.svg";
import { ReactComponent as OutlineChevronUp } from "assets/svg/outline-chevron-up.svg";
import { ReactComponent as OutlineChevronDown16 } from "assets/svg/outline-chevron-down-16.svg";
import Checkbox from "components/checkbox/Checkbox";
import CustomSelectionControl from "components/custom-selection-control/CustomSelectionControl";
import { useOrchidPro } from "components/Dashboard/DoctorDashboard/contexts/orchid-pro-context";
import DropdownToggle from "components/dropdown-toggle/DropdownToggle";
import ErrorMessage from "components/error-message";
import Loading from "components/Loading";
import RadioButton from "components/radio-button";
import SuccessMessage from "components/success-message";
import ToggleSwitch from "components/toggle-switch";
import { useClientsContext } from "contexts/ClientsContext";
import { useSubscription } from "contexts/subscription-context";
import { useTimeOnPageContext } from "contexts/time-on-page";
import { useUser } from "contexts/UserContext";
import { DAYS_OF_WEEK } from "constants/daysOfWeek";
import messages from "messages";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  Dropdown,
  Form,
  Row,
  Spinner,
  Tab,
  Tabs,
} from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import loginToStripe from "services/loginToStripe";
import putPatientProfile from "services/patient-chart/putPatientProfile";
import postOrchidPro from "services/postOrchidPro";
import { AmplitudePageName, track } from "utils/amplitude";
import getClientDisplayName from "utils/getClientDisplayName";
import searchClientsByName from "utils/searchClientsByName";
import ConnectWithStripe from "../../components/connect-with-stripe";
import styles from "./BillingSetting.module.scss";
import { useTeamContext } from "contexts/group-practice-context/TeamContext";

const AMPLITUDE_PAGE_NAME: AmplitudePageName = "pro-settings-billing";

const BILLING_SETTING_OPTIONS = [
  { value: "billing", label: "Billing" },
  { value: "insurance", label: "Insurance" },
];

export const INVOICE_CREATE_MODES = [
  {
    value: "daily",
    label: "Daily",
    description:
      "Generate invoices at the end of each day for any completed sessions.",
  },
  {
    value: "monthly",
    label: "Monthly",
    description:
      "Generate all invoices on the first day of each month for a consolidated process.",
  },
  {
    value: "manual",
    label: "Manually",
    description: "Create invoices only when you need them.",
  },
];

const BillingSetting = () => {
  const { setPage } = useTimeOnPageContext();
  useEffect(() => setPage(AMPLITUDE_PAGE_NAME), [setPage]);

  const { user } = useUser();

  const { canAccessFeature } = useSubscription();

  const navigate = useNavigate();
  const location = useLocation();

  const { activeTab } = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return {
      activeTab: query.get("tab") || "",
    };
  }, [location.search]);

  const [tab, setTab] = useState(activeTab || "billing");
  const [changed, setChanged] = useState(false);
  const [invoiceCreatedMode, setInvoiceCreatedMode] = useState("manual");
  const [superbillsCreatedMode, setSuperbillsCreatedMode] = useState("weekly");

  const switchAutoPay = () => {
    setChanged(true);
    setAutoPay((s) => !s);
  };
  const onInvoiceCreatedModeChange = (event) => {
    setChanged(true);
    const inputField = event.currentTarget;
    setInvoiceCreatedMode(inputField.value);
  };
  const onSuperbillsCreatedModeChange = (event) => {
    setChanged(true);
    const inputField = event.currentTarget;
    setSuperbillsCreatedMode(inputField.value);
  };

  const [autoPay, setAutoPay] = useState<boolean>(false);
  const [error, setError] = useState<any>();
  const [success, setSuccess] = useState<string>();

  const { practiceOwnerOrchidUserId } = useTeamContext();

  const { orchidPro, setOrchidPro } = useOrchidPro();

  const {
    individuals,
    couples,
    initializeClients,
    isLoadingClients,
    refreshClients,
  } = useClientsContext();

  useEffect(() => {
    initializeClients();
  }, [initializeClients]);

  const [query, setQuery] = useState("");
  const [showAllClients, setShowAllClients] = useState(false);

  const clients = useMemo(
    () => [...individuals, ...couples],
    [individuals, couples],
  );

  const clientsFiltered = useMemo(() => {
    return searchClientsByName(clients, query)
      .sort((a, b) =>
        getClientDisplayName(a) < getClientDisplayName(b) ? -1 : 1,
      )
      ?.filter((client) => client.patient.client_status === "patient");
  }, [clients, query]);

  const displayedClients = showAllClients
    ? clientsFiltered
    : clientsFiltered.slice(0, 15);

  const toggleShowAllClients = () => {
    setShowAllClients((prev) => !prev);
  };

  const [selectedClients, setSelectedClients] = useState<Set<string>>(
    new Set(),
  );

  useEffect(() => {
    if (!clientsFiltered) {
      return;
    }

    const initialSelectedClients = new Set(
      clientsFiltered
        .filter((client) => client.autopay_enabled)
        .map((client) => client.patient.orchid_user_id),
    );

    setSelectedClients(initialSelectedClients);

    if (orchidPro?.autopay_enabled !== undefined) {
      setAutoPay(orchidPro.autopay_enabled);
    }

    if (orchidPro?.billing_automation) {
      setInvoiceCreatedMode(orchidPro.billing_automation);
    }

    if (orchidPro?.autobatch_superbills) {
      if (orchidPro.autobatch_superbills === "weekly") {
        setSuperbillsCreatedMode("weekly");

        if (orchidPro?.weekly_autobatch_superbills_day) {
          setSelectedDay(orchidPro.weekly_autobatch_superbills_day);
        }
      } else if (orchidPro.autobatch_superbills === "monthly") {
        setSuperbillsCreatedMode("monthly");

        if (orchidPro?.monthly_autobatch_superbills_day) {
          setSelectedMonthDay(orchidPro.monthly_autobatch_superbills_day);
        }
      } else if (orchidPro.autobatch_superbills === "manual") {
        setSuperbillsCreatedMode("manual");
      }
    }

    if (orchidPro?.include_superbill_signature_line !== undefined) {
      setIncludeSignatureLine(orchidPro.include_superbill_signature_line);
    }
  }, [clientsFiltered, orchidPro]);

  const updatePatientProfiles = async () => {
    const serverEnabledClients = new Set(
      clientsFiltered
        .filter((client) => client.autopay_enabled)
        .map((client) => client.patient.orchid_user_id),
    );

    const addedClients = new Set(
      [...selectedClients].filter(
        (clientId) => !serverEnabledClients.has(clientId),
      ),
    );

    const removedClients = new Set(
      [...serverEnabledClients].filter(
        (clientId) => !selectedClients.has(clientId),
      ),
    );

    if (addedClients.size > 0) {
      for (let clientId of addedClients) {
        try {
          await putPatientProfile(
            clientId,
            {
              autopay_enabled: true,
            },
            practiceOwnerOrchidUserId,
          );
        } catch (error) {
          setError(error);
        }
      }
    }

    if (removedClients.size > 0) {
      for (let clientId of removedClients) {
        try {
          await putPatientProfile(
            clientId,
            {
              autopay_enabled: false,
            },
            practiceOwnerOrchidUserId,
          );
        } catch (error) {
          setError(error);
        }
      }
    }
    refreshClients();
  };

  const [busy, setBusy] = useState(false);

  const saveBillingSettings = async () => {
    setBusy(true);
    try {
      await updatePatientProfiles();
      await postOrchidProSettings();

      setSuccess("Saved");
    } catch (error) {
      setError(error);
    } finally {
      setChanged(false);
      setBusy(false);
    }
  };

  const postOrchidProSettings = async () => {
    try {
      setOrchidPro(
        await postOrchidPro(
          {
            ...orchidPro,
            autopay_enabled: autoPay,
            billing_automation: INVOICE_CREATE_MODES.find(
              (mode) => mode.value === invoiceCreatedMode,
            )?.value,
            autobatch_superbills: superbillsCreatedMode,
            ...(superbillsCreatedMode === "weekly" && {
              weekly_autobatch_superbills_day: selectedDay,
            }),
            ...(superbillsCreatedMode === "monthly" && {
              monthly_autobatch_superbills_day: selectedMonthDay,
            }),
            include_superbill_signature_line: includeSignatureLine,
          },
          practiceOwnerOrchidUserId,
        ),
      );
    } catch (error) {
      setError(error);
    }
  };

  const handleClientSelect = useCallback(
    (clientId) => (e) => {
      setSelectedClients((prev) => {
        const newSet = new Set(prev);
        if (e.target.checked) {
          newSet.add(clientId);
        } else {
          newSet.delete(clientId);
        }
        setChanged(true);
        return newSet;
      });
    },
    [],
  );

  const clearAllSelections = () => {
    setSelectedClients(new Set());
    setChanged(true);
    track(AMPLITUDE_PAGE_NAME, "autopay-enable-all-clear", "click");
  };

  const goToStripe = async () => {
    try {
      const { url } = await loginToStripe();
      window.open(url, "_blank");
    } catch (error) {
      setError(error);
    }
  };

  const [selectedDay, setSelectedDay] = useState(0);
  const [selectedMonthDay, setSelectedMonthDay] = useState(1);
  const [includeSignatureLine, setIncludeSignatureLine] =
    useState<boolean>(false);

  const switchSignatureLine = () => {
    setChanged(true);
    setIncludeSignatureLine((s) => !s);
  };

  return (
    <div className={styles["billing-setting"]}>
      {error && (
        <ErrorMessage
          show={true}
          onHide={() => setError(undefined)}
          errorMsg={error?.toString()}
        />
      )}
      {success && (
        <SuccessMessage
          show={true}
          onHide={() => setSuccess(undefined)}
          message={"Saved"}
          duration={1000}
        />
      )}
      <div className={styles["billing-setting__header"]}>
        <h4 className={"rg-32 deep-blue questrial mb-0"}>Billing</h4>
        <Tabs
          activeKey={tab}
          onSelect={(k) => {
            setTab((s) => k || s);
            navigate(`/pro-dashboard/settings/billing?tab=${k}`, { replace: true });
          }}
          className={styles["billing-setting__tabs"]}
        >
          {BILLING_SETTING_OPTIONS.map((el, i) => (
            <Tab
              key={i}
              eventKey={el.value}
              title={el.label}
              tabClassName={styles["billing-setting__tab"]}
              onEnter={() =>
                track(AMPLITUDE_PAGE_NAME, "billing-tab", "click", {
                  eventProperties: { tab: el.value },
                })
              }
            ></Tab>
          ))}
        </Tabs>
      </div>

      <div className={styles["billing-setting__body"]}>
        {tab === "billing" && (
          <>
            <div className={styles["billing-setting__section"]}>
              <div className={styles["billing-setting__section-title"]}>
                Billing Methods
              </div>
              <div className={"mb-4"}>
                Orchid provides two separate ways to charge patients.
              </div>
              <div className={"bd-16 mb-2"}>Credit card Authorization</div>
              <div>
                A credit card authorization is a pending charge initiated by
                Orchid to ensure the patient's card works and that there are
                enough funds available to pay for their sessions. The patient
                will not be charged if you are unable to make the session. This
                feature is enabled by selecting <b>Yes</b> in{" "}
                <b>Prevent No-shows</b> section when creating a new session.
              </div>
              <hr className={styles["billing-setting__hr"]} />
              <div className={"bd-16 mb-2"}>Auto-Generate Invoices</div>
              <div className={"mb-4"}>
                Let your invoicing run on autopilot with flexible options
                tailored to your needs:
              </div>
              <div className={styles["billing-setting__invoice-created"]}>
                <div className={"bd-16 mb-4"}> How are invoices created?</div>
                {INVOICE_CREATE_MODES.map((option, i) => (
                  <RadioButton
                    key={i}
                    className={styles["billing-setting__radio"]}
                    id={option.value}
                    label={option.label}
                    value={option.value}
                    name="invoice-created-mode-option"
                    onChange={onInvoiceCreatedModeChange}
                    checked={invoiceCreatedMode === option.value}
                    onClick={() => {
                      track(
                        AMPLITUDE_PAGE_NAME,
                        "invoice-creation-mode-radio",
                        "click",
                      );
                    }}
                  />
                ))}
                <div>
                  <b>Note:</b> Selecting an automatic invoicing schedule will
                  not send invoices to clients automatically.
                </div>
              </div>
              <hr className={styles["billing-setting__hr"]} />
              <div className={"bd-16 mb-2"}>Invoice Past Due</div>
              <div className={"d-flex align-items-center"}>
                <Checkbox
                  variant={"rectangle"}
                  disabled
                  onClick={() =>
                    track(
                      AMPLITUDE_PAGE_NAME,
                      "invoice-past-due-email-checkbox",
                      "click",
                    )
                  }
                />
                <span>Email patients when an invoice is past due.</span>
              </div>
            </div>
            {canAccessFeature("autopay") && (
              <div className={styles["billing-setting__section"]}>
                <div className={styles["billing-setting__section-title"]}>
                  AutoPay
                </div>
                <div className={"mb-4"}>
                  Automatically collect payments for outstanding invoice
                  balances using the patient's default credit card on file.
                </div>
                <div className={"bd-16 mb-2"}>Enable AutoPay?</div>
                <div className={"mb-4 gray01"}>
                  AutoPay will attempt to pay any outstanding unpaid invoices on
                  a daily basis, between midnight and 2 AM local time
                </div>
                <div className={styles["billing-setting__settings-input"]}>
                  <span aria-hidden="true">{messages.off}</span>
                  <ToggleSwitch
                    checked={autoPay}
                    onChange={switchAutoPay}
                    onClick={() =>
                      track(AMPLITUDE_PAGE_NAME, "autopay-toggle", "click")
                    }
                  />
                  <span aria-hidden="true">{messages.on}</span>
                </div>

                <div className={styles["billing-setting__autopay-note"]}>
                  This feature only works with{" "}
                  {user?.has_stripe_account_connected ? (
                    <Button
                      className={styles["billing__button"]}
                      variant="link"
                      size="sm"
                      onClick={() => {
                        track(
                          AMPLITUDE_PAGE_NAME,
                          "go-to-stripe-button",
                          "click",
                        );
                        goToStripe();
                      }}
                    >
                      Stripe payments
                    </Button>
                  ) : (
                    <ConnectWithStripe
                      title={"Stripe payments"}
                      amplitudePageName={AMPLITUDE_PAGE_NAME}
                      amplitudeComponentName={"stripe-payments-link"}
                      className={"p-0"}
                      variant={"link"}
                    />
                  )}
                </div>
                <div>
                  <b>Note:</b> If AutoPay isn't working for a client, be sure to
                  have AutoPay turned on at the patient level.
                  <div style={{ marginLeft: 40 }}>
                    Go to{" "}
                    <b>{`Patient List > [Client Name] > Billing > Setting > Online Payment Method`}</b>{" "}
                    to verify that it's turned on and a default credit card is
                    on file.
                  </div>
                </div>
                <hr className={styles["billing-setting__hr"]} />
                <div className={styles["billing-setting__enrolled-header"]}>
                  <div
                    className={styles["billing-setting__enrolled-header-left"]}
                  >
                    <div className={"bd-16"}>Enrolled in Autopay</div>
                    <Button
                      variant={"link"}
                      className={"p-0"}
                      onClick={clearAllSelections}
                    >
                      Clear All
                    </Button>
                  </div>
                  <Form.Control
                    className={styles["billing-setting__search-input"]}
                    type="text"
                    placeholder="Search"
                    onChange={(event) => setQuery(event.currentTarget.value)}
                    value={query}
                  />
                </div>

                {isLoadingClients ? (
                  <Loading />
                ) : (
                  <div className={styles["list-scroll-container"]}>
                    <Row md={3}>
                      {displayedClients.map((client) => (
                        <Col
                          className={styles["enrolled-client-col"]}
                          key={client.patient.orchid_user_id}
                        >
                          <CustomSelectionControl
                            className={styles["list-view__checkbox"]}
                            type={"checkbox"}
                            checked={selectedClients?.has(
                              client.patient.orchid_user_id,
                            )}
                            onChange={handleClientSelect(
                              client.patient.orchid_user_id,
                            )}
                          />
                          <span>{getClientDisplayName(client)}</span>
                        </Col>
                      ))}
                    </Row>
                    <div className={"d-flex justify-content-center"}>
                      {clientsFiltered.length > 15 && (
                        <Button
                          variant={"link"}
                          className={styles["view-all-button"]}
                          onClick={toggleShowAllClients}
                        >
                          {showAllClients ? (
                            <>
                              <OutlineChevronUp /> Show Less
                            </>
                          ) : (
                            <>
                              <OutlineChevronDown /> View All
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className={styles["billing-setting__section"]}>
              <div className={styles["billing-setting__section-title"]}>
                Auto-generate Superbills
              </div>
              <div className={"mb-4"}>
                Let Orchid generate superbills on a schedule that fits your
                workflow. You can customize settings for individual clients by
                going to <b>{`Client List > [Client Name] > Client Info`}</b>.
              </div>
              <div className={styles["billing-setting__invoice-created"]}>
                <div className={"bd-16 mb-4"}>How are Superbills created?</div>
                <div className={styles["billing-setting__radio"]}>
                  <RadioButton
                    id="weekly"
                    value="weekly"
                    name="superbills-created-mode-option"
                    onChange={onSuperbillsCreatedModeChange}
                    checked={superbillsCreatedMode === "weekly"}
                    onClick={() => {
                      track(
                        AMPLITUDE_PAGE_NAME,
                        "superbills-creation-mode-radio",
                        "click",
                      );
                    }}
                  />
                  <div className={styles["billing-setting__radio-label"]}>
                    <b>Weekly: </b> Generate superbills on every
                    <DropdownToggle
                      title={DAYS_OF_WEEK[selectedDay]}
                      Caret={<OutlineChevronDown16 />}
                    >
                      {DAYS_OF_WEEK.map((day, index) => (
                        <Dropdown.Item
                          key={index}
                          onClick={() => {
                            setChanged(true);
                            setSelectedDay(index);
                          }}
                        >
                          {day}
                        </Dropdown.Item>
                      ))}
                    </DropdownToggle>
                    of the following week.
                  </div>
                </div>
                <div className={styles["billing-setting__radio"]}>
                  <RadioButton
                    id="monthly"
                    value="monthly"
                    name="superbills-created-mode-option"
                    onChange={onSuperbillsCreatedModeChange}
                    checked={superbillsCreatedMode === "monthly"}
                    onClick={() => {
                      track(
                        AMPLITUDE_PAGE_NAME,
                        "superbills-creation-mode-radio",
                        "click",
                      );
                    }}
                  />
                  <div className={styles["billing-setting__radio-label"]}>
                    <b>Monthly: </b> Generate superbills on day
                    <DropdownToggle
                      title={selectedMonthDay}
                      Caret={<OutlineChevronDown16 />}
                    >
                      {Array.from({ length: 21 }, (_, i) => (
                        <Dropdown.Item
                          key={i + 1}
                          onClick={() => {
                            setChanged(true);
                            setSelectedMonthDay(i + 1);
                          }}
                        >
                          {i + 1}
                        </Dropdown.Item>
                      ))}
                    </DropdownToggle>
                    of the following month.
                  </div>
                </div>
                <div className={styles["billing-setting__radio"]}>
                  <RadioButton
                    id="manual"
                    value="manual"
                    name="superbills-created-mode-option"
                    onChange={onSuperbillsCreatedModeChange}
                    checked={superbillsCreatedMode === "manual"}
                    onClick={() => {
                      track(
                        AMPLITUDE_PAGE_NAME,
                        "superbills-creation-mode-radio",
                        "click",
                      );
                    }}
                  />
                  <div className={styles["billing-setting__radio-label"]}>
                    <b>Manually: </b> Create superbills only when you need them.
                  </div>
                </div>

                <div>
                  <b>Note:</b> Once an individual client's setting is
                  customized, that setting becomes independent and is not
                  overridden by changes to the global setting. If you want to
                  reset all clients to the new global setting:
                </div>
                <Button
                  disabled
                  variant={"link"}
                  className={"px-0 pt-3"}
                  onClick={() => {
                    track(
                      AMPLITUDE_PAGE_NAME,
                      "reset-all-clients-button",
                      "click",
                    );
                  }}
                >
                  Reset all clients to global default
                </Button>
              </div>
              <div className={styles["billing-setting__signature-line-title"]}>
                Superbill format
              </div>
              <div className={styles["billing-setting__signature-line"]}>
                <CustomSelectionControl
                  type={"checkbox"}
                  checked={includeSignatureLine}
                  onChange={switchSignatureLine}
                  onClick={() =>
                    track(
                      AMPLITUDE_PAGE_NAME,
                      "signature-line-checkbox",
                      "click",
                    )
                  }
                />
                <span>Include signature line</span>
              </div>
            </div>
          </>
        )}
      </div>

      <div className={styles["billing-setting__footer"]}>
        <div className={"gray01 rg-12"}>
          ©2026 Orchid Exchange Inc. All rights reserved
        </div>
        <Button
          variant="primary"
          onClick={() => {
            track(AMPLITUDE_PAGE_NAME, "save-button", "click");
            saveBillingSettings();
          }}
          className={styles["data-analysis__save-button"]}
          disabled={!changed || busy}
        >
          Save{" "}
          {busy && (
            <Spinner
              as="span"
              animation="grow"
              size="sm"
              role="status"
              aria-hidden="true"
              className="ml-3"
            />
          )}
        </Button>
      </div>
    </div>
  );
};

export default BillingSetting;
